import { useCallback, useEffect, useRef, useState } from 'react';
import { safeStableStringify } from './string-utils';
/**
 * useQuery er en React-hook for å hente og cache asynkrone data.
 *
 * - Lokal cache per input (kun i denne komponent-instansen), med mulighet for å sette hvor lenge data anses som ferskt
 * - Deduplisering av forespørsler som allerede er i gang
 * - Avbryter requests som ikke trengs mer fordi input er endret
 * - Ignorerer requests som kommer for sent tilbake (input er endret i mens)
 * - Manuell refetch
 * - Mulig å beholde forrige data mens ny hentes
 * - Støtte for paginering/infinite scroll ved å kunne nye resultater inn i gamle
 * - Støtte for placeholderdata mens data lastes
 *
 * @param options Objekt med options, se UseQueryOptions
 */
export function useQuery(options) {
    const { queryKey, queryFn, enabled = true, staleTime = 0, keepPreviousData = true, onSuccess, onError, args = [], merge: mergeFn, placeholderData, } = options;
    const keyString = Array.isArray(queryKey) ? safeStableStringify(queryKey) : String(queryKey);
    const cacheRef = useRef(new Map());
    const requestIdRef = useRef(0);
    const [, forceRender] = useState({});
    const prevDataRef = useRef(undefined);
    const getEntry = useCallback(() => {
        let entry = cacheRef.current.get(keyString);
        if (!entry) {
            entry = { state: { loading: false } };
            cacheRef.current.set(keyString, entry);
        }
        return entry;
    }, [keyString]);
    const isFresh = useCallback((entry) => {
        if (!entry.state.updatedAt) {
            return false;
        }
        if (staleTime === 0) {
            return false;
        }
        return Date.now() - entry.state.updatedAt < staleTime;
    }, [staleTime]);
    const startFetch = useCallback((manual = false, overrideArgs, mergeMode = false) => {
        if (!enabled && !manual) {
            return;
        }
        const entry = getEntry();
        if (!manual && isFresh(entry)) {
            return;
        }
        // Dedupliser requests
        if (entry.promise) {
            return;
        }
        if (!manual && entry.state.error && entry.state.updatedAt) {
            const timeSinceError = Date.now() - entry.state.updatedAt;
            if (timeSinceError < staleTime) {
                return; // Don't retry errors within staleTime window
            }
        }
        const currentRequestId = ++requestIdRef.current;
        // Avbryt forrige forespørsel
        // @todo Dette fører til en AbortError, som må håndteres av oss spesielt siden vi nok ikke vil logge dette
        if (entry.abort) {
            entry.abort.abort();
        }
        const abort = new AbortController();
        entry.abort = abort;
        // Lag placeholder-data
        const usedArgs = overrideArgs ?? args;
        let placeholder;
        if (entry.state.data === undefined && placeholderData !== undefined) {
            placeholder =
                typeof placeholderData === 'function'
                    ? placeholderData(prevDataRef.current, { queryKey, args: usedArgs })
                    : placeholderData;
        }
        const preserved = keepPreviousData ? entry.state.data : undefined;
        entry.state = {
            ...entry.state,
            loading: true,
            error: undefined,
            data: preserved !== undefined ? preserved : placeholder,
        };
        forceRender({});
        const p = queryFn(abort.signal, ...usedArgs)
            .then(data => {
            if (abort.signal.aborted || currentRequestId !== requestIdRef.current) {
                // Ignorer avbrutte og foreldede forespørsler
                return data;
            }
            // Slå sammen data fra flere forespørsler
            const nextData = mergeMode && mergeFn ? mergeFn(entry.state.data, data) : data;
            entry.state = {
                data: nextData,
                error: undefined,
                updatedAt: Date.now(),
                loading: false,
            };
            prevDataRef.current = nextData;
            onSuccess?.(data);
            return data;
        })
            .catch(err => {
            if (abort.signal.aborted || currentRequestId !== requestIdRef.current) {
                return;
            }
            entry.state = {
                ...entry.state,
                error: err,
                updatedAt: Date.now(),
                loading: false,
            };
            onError?.(err);
        })
            .finally(() => {
            if (entry.promise === p) {
                entry.promise = undefined;
                forceRender({});
            }
        });
        entry.promise = p;
    }, [getEntry, enabled, keepPreviousData, queryFn, args, onSuccess, onError, isFresh, mergeFn, placeholderData, queryKey, staleTime]);
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- @todo Rett opp i
        startFetch();
    }, [keyString, startFetch]);
    useEffect(() => {
        const mapRef = cacheRef.current;
        return () => {
            mapRef.forEach(e => e.abort?.abort());
        };
    }, []);
    const refetch = useCallback(() => startFetch(true), [startFetch]);
    const fetchNext = useCallback((nextArgs) => startFetch(true, nextArgs, true), [startFetch]);
    // eslint-disable-next-line react-hooks/refs -- @todo Må rettes opp i
    const entry = getEntry();
    const data = entry.state.data;
    const isStale = !isFresh(entry);
    let status = 'idle';
    if (entry.state.loading) {
        status = 'loading';
    }
    else if (entry.state.error) {
        status = 'error';
    }
    else if (entry.state.data !== undefined) {
        status = 'success';
    }
    return {
        data,
        error: entry.state.error,
        isLoading: entry.state.loading,
        isError: entry.state.error !== undefined,
        dataUpdatedAt: entry.state.updatedAt,
        isStale,
        refetch,
        status,
        fetchNext,
    };
}
